import * as React from 'react';
import { ISubentity } from "../../../entities/Subentities";
import { Dictionary } from '../../../entities/common';
import GenericUpdatePanel, { GenericPanelProps } from './UpdatePanel';
import * as Metadata from '../../../entities/Metadata';

export type Props<TSubentity extends ISubentity> = GenericPanelProps<TSubentity> & {
    buildNewEntity?: () => TSubentity;
    onComplete: (entity: TSubentity, callback?: (entity: TSubentity) => void) => void;
}

const CreatePanel = <TSubentity extends ISubentity>(props: Props<TSubentity>) => {
    const UpdatePanel = React.useMemo(() => GenericUpdatePanel<TSubentity>(), []);
    const entity = React.useMemo(() => {
        const raw = props.buildNewEntity?.() ?? { attributes: {} } as TSubentity;
        const system = (typeof props.fields === 'function' ? props.fields(raw) : props.fields)
            .filter(Metadata.isRequired).reduce((a, f) => ({ ...a, [f.name]: undefined }), {});
        return { ...raw, attributes: { ...system, ...raw.attributes } };
    }, []);
    // On CREATE the payload IS the new entity (there is no stored entity for the server to merge a minimal change
    // onto), so it must be self-consistent: include the DERIVED overlay, not just the raw edits. Without it a
    // superseded key would fall back to its `buildNewEntity` default and contradict the others — e.g. editing Duration
    // clears the now-stale DueDate from `changes`, and the default 1-day DueDate would be sent alongside Duration=5.
    // The overlay comes from the panel (`derived`) rather than being re-derived here, so the created entity is exactly
    // what the panel displayed — re-deriving would need the panel's edit order too, and could disagree without it.
    const onComplete = React.useCallback(
        (id: string, changes: Dictionary<unknown>, derived?: Dictionary<unknown>) => props.onComplete({
            ...entity,
            attributes: { ...entity.attributes, ...changes, ...derived }
        }),
        [props.onComplete, entity]);

    const subentityTypeLabel = typeof props.subentityTypeLabel === 'function' ? props.subentityTypeLabel(entity) : props.subentityTypeLabel;
    const header = React.useMemo(
        () => ({
            ...props.header,
            secondaryText: `Fill in the fields below to create new ${subentityTypeLabel}`
        }),
        [props.header, subentityTypeLabel]);
    return <UpdatePanel
        {...props}
        subentityTypeLabel={subentityTypeLabel}
        header={header}
        operation="Create"
        entity={entity}
        onComplete={onComplete}
    />;
}

export default CreatePanel;